import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Plus, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface ProtectionLog {
  id: string;
  date: string;
  created_at: string;
  protection_methods: string[];
  grounding_techniques: string[];
  boundary_setting: string;
  before_energy_state: string;
  after_energy_state: string;
  effectiveness_rating: number;
  notes: string;
}

const protectionMethods = [
  { id: 'white-light', label: 'White Light Visualisation' },
  { id: 'shield', label: 'Energy Shield' },
  { id: 'sage', label: 'Sage/Smoke Cleansing' },
  { id: 'salt', label: 'Salt Protection' },
  { id: 'crystals', label: 'Protective Crystals' },
  { id: 'prayer', label: 'Prayer/Invocation' },
  { id: 'mirror', label: 'Mirror Technique' },
  { id: 'archangel', label: 'Archangel Michael' }
];

const groundingTechniques = [
  { id: 'barefoot', label: 'Barefoot on Earth' },
  { id: 'tree', label: 'Tree Visualisation' },
  { id: 'cord', label: 'Grounding Cord' },
  { id: 'breath', label: 'Grounding Breath' },
  { id: 'stone', label: 'Holding Stones' },
  { id: 'food', label: 'Eating Root Vegetables' }
];

export default function PsychicProtection() {
  const [logs, setLogs] = useState<ProtectionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    protection_methods: [] as string[],
    grounding_techniques: [] as string[],
    boundary_setting: '',
    before_energy_state: '',
    after_energy_state: '',
    effectiveness_rating: 5,
    notes: ''
  });

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('psychic_protection_log')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading logs', variant: 'destructive' });
    } else {
      setLogs(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('psychic_protection_log')
      .insert({
        user_id: user.id,
        date: new Date().toISOString().split('T')[0],
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving log', variant: 'destructive' });
    } else {
      toast({ title: 'Protection log saved' });
      setFormData({
        protection_methods: [],
        grounding_techniques: [],
        boundary_setting: '',
        before_energy_state: '',
        after_energy_state: '',
        effectiveness_rating: 5,
        notes: ''
      });
      setShowForm(false);
      loadLogs();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading protection log...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary animate-pulse" />
            Psychic Protection Log
          </CardTitle>
          <CardDescription>
            Track protection practices, grounding techniques, and energetic boundaries. Essential for empaths and sensitives to maintain healthy energy and prevent psychic overwhelm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log Protection Practice
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-6 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="space-y-3">
                <Label>Protection Methods Used</Label>
                <div className="grid grid-cols-2 gap-3">
                  {protectionMethods.map((method) => (
                    <div key={method.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={method.id}
                        checked={formData.protection_methods.includes(method.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ ...formData, protection_methods: [...formData.protection_methods, method.id] });
                          } else {
                            setFormData({ ...formData, protection_methods: formData.protection_methods.filter(id => id !== method.id) });
                          }
                        }}
                      />
                      <label htmlFor={method.id} className="text-sm cursor-pointer">
                        {method.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Grounding Techniques Used</Label>
                <div className="grid grid-cols-2 gap-3">
                  {groundingTechniques.map((technique) => (
                    <div key={technique.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={technique.id}
                        checked={formData.grounding_techniques.includes(technique.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ ...formData, grounding_techniques: [...formData.grounding_techniques, technique.id] });
                          } else {
                            setFormData({ ...formData, grounding_techniques: formData.grounding_techniques.filter(id => id !== technique.id) });
                          }
                        }}
                      />
                      <label htmlFor={technique.id} className="text-sm cursor-pointer">
                        {technique.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Energy State Before</Label>
                <Textarea
                  value={formData.before_energy_state}
                  onChange={(e) => setFormData({ ...formData, before_energy_state: e.target.value })}
                  placeholder="How did you feel before protection? Drained, anxious, overwhelmed, scattered?"
                  rows={2}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Energy State After</Label>
                <Textarea
                  value={formData.after_energy_state}
                  onChange={(e) => setFormData({ ...formData, after_energy_state: e.target.value })}
                  placeholder="How do you feel after protection? Centered, safe, clear, grounded?"
                  rows={2}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Effectiveness Rating: {formData.effectiveness_rating}/10</Label>
                <Slider
                  value={[formData.effectiveness_rating]}
                  onValueChange={([value]) => setFormData({ ...formData, effectiveness_rating: value })}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <Label>Boundary Setting</Label>
                <Textarea
                  value={formData.boundary_setting}
                  onChange={(e) => setFormData({ ...formData, boundary_setting: e.target.value })}
                  placeholder="Any boundaries set with people, situations, or energy? How did you assert your space?"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="What worked well? What didn't? Insights or adjustments needed?"
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Log</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start logging protection practices to discover what works best for you.
              </p>
            ) : (
              logs.map((log) => (
                <Card key={log.id} className="bg-muted/30 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <Badge variant="outline">Effectiveness: {log.effectiveness_rating}/10</Badge>
                        <div className="flex gap-2 flex-wrap">
                          {log.protection_methods.map((method, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{method}</Badge>
                          ))}
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(log.date), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-light text-primary mb-1">Before:</p>
                      <p className="text-sm text-muted-foreground font-light">{log.before_energy_state}</p>
                    </div>
                    <div>
                      <p className="text-sm font-light text-primary mb-1">After:</p>
                      <p className="text-sm text-muted-foreground font-light">{log.after_energy_state}</p>
                    </div>
                    {log.boundary_setting && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1">Boundaries:</p>
                        <p className="text-sm text-muted-foreground font-light">{log.boundary_setting}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
            <h4 className="font-light text-primary mb-2">🛡️ Protection Tips</h4>
            <ul className="text-sm space-y-1 text-muted-foreground font-light">
              <li>• Protect daily, especially before psychic work or being in crowds</li>
              <li>• Ground after readings to release energy</li>
              <li>• Set clear intention: "I am protected and only positive energy may enter"</li>
              <li>• Cleanse your space regularly with sage or sound</li>
              <li>• Trust your intuition - if something feels off, shield immediately</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
